import moment from "moment";
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  getTickets,
  deleteTicket,
  getBranchOfficesFiltered,
  getSkusFiltered,
  getClients,
  getClusters,
} from "../../api/Services";
import { hasPermission, privilegesEnum } from "../../helpers/PermissionHelper";
import {
  useDebouncedValue,
  useFetchRequestGuard,
} from "../../helpers/FilterSearchHelper";

import {
  Table,
  Button,
  message,
  Modal,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Dropdown,
  Menu,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  MoreOutlined,
  SearchOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { RxCardStack } from "react-icons/rx";
import PageHeader from "../../components/pageheader/PageHeader";
import EmptyState from "../../components/emptystate/EmptyState";

import styles from "./Tickets.module.scss";

const Tickets = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const getBasePath = () => {
    if (location.pathname.startsWith("/commercial-intelligence")) {
      return "/commercial-intelligence/tickets";
    }
    if (location.pathname.startsWith("/fidelity")) {
      return "/fidelity/tickets";
    }
    return "/promotions/tickets";
  };

  const yesterday = moment().subtract(1, "days").format("YYYY-MM-DD");

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSize, setTotalSize] = useState(0);
  const [actualPage, setActualPage] = useState(1);

  const { beginRequest, isStaleRequest } = useFetchRequestGuard();
  const [codeFilterValue, setCodeFilterValue] = useState<string>(searchParams.get("code") || "");
  const debouncedCodeFilterValue = useDebouncedValue(codeFilterValue);
  const [totalLessFilterValue, setTotalLessFilterValue] = useState<number | undefined>(
    searchParams.get("minTotal") ? Number(searchParams.get("minTotal")) : undefined
  );
  const [totalGreaterFilterValue, setTotalGreaterFilterValue] = useState<number | undefined>(
    searchParams.get("maxTotal") ? Number(searchParams.get("maxTotal")) : undefined
  );
  const [branchOffices, setBranchOffices] = useState<any[]>([]);
  const [branchOfficeFilterValue, setBranchOfficeFilterValue] = useState<number | undefined>(
    searchParams.get("branchOfficeId") ? Number(searchParams.get("branchOfficeId")) : undefined
  );
  const [clusters, setClusters] = useState<any[]>([]);
  const [clusterFilterValue, setClusterFilterValue] = useState<number | undefined>(
    searchParams.get("clusterId") ? Number(searchParams.get("clusterId")) : undefined
  );
  function isValidDateString(dateStr: string | null) {
    return !!dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  }

  const urlStartDate = searchParams.get("startDate");
  const urlEndDate = searchParams.get("endDate");
  const [actualFilterStartDate, setActualFilterStartDate] = useState<any>(
    isValidDateString(urlStartDate) ? urlStartDate : yesterday
  );
  const [actualFilterEndDate, setActualFilterEndDate] = useState<any>(
    isValidDateString(urlEndDate) ? urlEndDate : yesterday
  );
  const [skuOptions, setSkuOptions] = useState<any[]>([]);
  const [skusSelected, setSkusSelected] = useState<any[]>(
    searchParams.get("skuIds") ? searchParams.get("skuIds")!.split(",").map(Number) : []
  );
  const [documentTypeFilterValue, setDocumentTypeFilterValue] = useState<string | undefined>(
    searchParams.get("documentType") || undefined
  );
  const [clientOptions, setClientOptions] = useState<any[]>([]);
  const [clientFilterValue, setClientFilterValue] = useState<number | undefined>(
    searchParams.get("clientId") ? Number(searchParams.get("clientId")) : undefined
  );

  useEffect(() => {
    const params: any = {};
    if (codeFilterValue) params.code = codeFilterValue;
    if (branchOfficeFilterValue) params.branchOfficeId = branchOfficeFilterValue;
    if (clusterFilterValue) params.clusterId = clusterFilterValue;
    if (actualFilterStartDate) params.startDate = moment(actualFilterStartDate).format("YYYY-MM-DD");
    if (actualFilterEndDate) params.endDate = moment(actualFilterEndDate).format("YYYY-MM-DD");
    if (totalLessFilterValue !== undefined) params.minTotal = totalLessFilterValue;
    if (totalGreaterFilterValue !== undefined) params.maxTotal = totalGreaterFilterValue;
    if (skusSelected && skusSelected.length > 0) params.skuIds = skusSelected.join(",");
    if (documentTypeFilterValue) params.documentType = documentTypeFilterValue;
    if (clientFilterValue) params.clientId = clientFilterValue;
    setSearchParams(params);
  }, [codeFilterValue, branchOfficeFilterValue, clusterFilterValue, actualFilterStartDate, actualFilterEndDate, totalLessFilterValue, totalGreaterFilterValue, skusSelected, documentTypeFilterValue, clientFilterValue, setSearchParams]);

  useEffect(() => {
    handleSearchBranchOffices("");
    handleSearchClusters("");
  }, []);

  const handleSearchBranchOffices = async (value: string) => {
    const response = await getBranchOfficesFiltered(0, {
      name: value,
    });
    setBranchOffices(response.branchOffices);
  };

  const handleSearchClusters = async (value: string) => {
    const response = await getClusters(0);
    setClusters(response.clusters);
  };

  const handleFetchTickets = useCallback(async (pagination = 1) => {
    const requestId = beginRequest();
    setLoading(true);
    const response = await getTickets(pagination - 1, {
      code: debouncedCodeFilterValue,
      branchOfficeId: branchOfficeFilterValue,
      clusterId: clusterFilterValue,
      skuIds: skusSelected,
      minTotal: totalLessFilterValue,
      maxTotal: totalGreaterFilterValue,
      documentType: documentTypeFilterValue,
      clientId: clientFilterValue,
      startDate: actualFilterStartDate
        ? moment(actualFilterStartDate, "YYYY-MM-DD").format("YYYY-MM-DD")
        : undefined,
      endDate: actualFilterEndDate
        ? moment(actualFilterEndDate, "YYYY-MM-DD").format("YYYY-MM-DD")
        : undefined,
    });

    if (isStaleRequest(requestId)) return;

    setData(response.tickets);
    setTotalSize(response.totalElements);
    setLoading(false);
  }, [debouncedCodeFilterValue, branchOfficeFilterValue, clusterFilterValue, skusSelected, totalLessFilterValue, totalGreaterFilterValue, documentTypeFilterValue, clientFilterValue, actualFilterStartDate, actualFilterEndDate, beginRequest, isStaleRequest]);

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_TICKETS)) {
      handleFetchTickets();
    }
  }, [handleFetchTickets]);

  const handleSearchSkus = async (value: string) => {
    const response = await getSkusFiltered(0, { skuName: value });
    setSkuOptions(response.skus);
  };

  const handleSearchClients = async (value: string) => {
    const response = await getClients(0, { idNumber: value });
    setClientOptions(response.clients);
  };

  const showDeleteConfirm = (itemId: number) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDelete(itemId);
      },
    });
  };

  const handleDelete = async (ticketId: number) => {
    setLoading(true);
    const deleteStatus = await deleteTicket(ticketId);
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await handleFetchTickets();
    setLoading(false);
  };

  const handlePagination = (page: number) => {
    setActualPage(page);
    handleFetchTickets(page);
  };

  const handleClearFilters = () => {
    setCodeFilterValue("");
    setBranchOfficeFilterValue(undefined);
    setActualFilterStartDate(yesterday);
    setActualFilterEndDate(yesterday);
    setTotalLessFilterValue(undefined);
    setTotalGreaterFilterValue(undefined);
    setSkusSelected([]);
    setDocumentTypeFilterValue(undefined);
    setClientFilterValue(undefined);
    setActualPage(1);
    handleFetchTickets(1);
  };

  const hasActiveFilters = () => {
    return (
      codeFilterValue !== "" ||
      branchOfficeFilterValue !== undefined ||
      clusterFilterValue !== undefined ||
      actualFilterStartDate !== yesterday ||
      actualFilterEndDate !== yesterday ||
      totalLessFilterValue !== undefined ||
      totalGreaterFilterValue !== undefined ||
      (skusSelected && skusSelected.length > 0) ||
      documentTypeFilterValue !== undefined ||
      clientFilterValue !== undefined
    );
  };

  const hasNoData = () => {
    return !loading && data.length === 0;
  };

  const hasNoDataDueToFilters = () => {
    return hasNoData() && hasActiveFilters();
  };

  const hasNoDataAtAll = () => {
    return hasNoData() && !hasActiveFilters();
  };

  const getEmptyState = () => {
    if (hasNoDataDueToFilters()) {
      return <EmptyState type="noResults" entityName="Ticket" />;
    }

    if (hasNoDataAtAll()) {
      return (
        <EmptyState
          type="noData"
          entityName="Ticket"
        />
      );
    }

    return null;
  };
  const handleChangeDateFilter = (value: any) => {
    setActualFilterStartDate(value ? value[0] ?? null : null);
    setActualFilterEndDate(value ? value[1] ?? null : null);
  };
  const getOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const columns = [
    {
      title: t("Common-Code"),
      dataIndex: "code",
      key: "code",
      render: (code: string, record: any) => (
        <a
          href={`${getBasePath()}/${record.id}/view`}
          className={styles.nameLink}
        >
          {code}
        </a>
      ),
    },
    {
      title: t("Common-Date"),
      dataIndex: "date",
      key: "date",
      render: (date: any) => moment(date, "YYYY-MM-DD").format("DD-MM-YYYY"),
    },
    {
      title: t("Common-Type"),
      render: (ticket: any) => ticket.documentType.description_es,
      key: "documentType",
    },
    {
      title: t("Common-BranchOffice"),
      render: (ticket: any) => (
        <a
          href={`/admin/branchoffices/${ticket.salePoint.branchOffice?.id}/view`}
          className={styles.nameLink}
        >
          {ticket.salePoint.branchOffice?.name}
        </a>
      ),
      key: "branchOffice",
    },
    {
      title: t("Common-Client"),
      render: (ticket: any) => {
        if (!ticket.client) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        const displayText = ticket.client.idNumber || 
                           ticket.client.externalId || 
                           `Cliente ${ticket.client.id}`;
        return (
          <a
            href={`${getBasePath().replace('/tickets', '')}/clients/${ticket.client.id}/view`}
            className={styles.nameLink}
          >
            {displayText}
          </a>
        );
      },
      key: "client",
    },
    {
      title: t("Common-Total"),
      dataIndex: "total",
      key: "total",
      render: (total: number) => `$${total?.toLocaleString('es-ES')}`,
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (text: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="view"
                icon={<EyeOutlined />}
                onClick={() => navigate(`${getBasePath()}/${record.id}/view`)}
              >
                {t("Common-View")}
              </Menu.Item>
              {hasPermission(privilegesEnum.DELETE_TICKETS) && (
                <Menu.Item
                  key="delete"
                  icon={<DeleteOutlined />}
                  onClick={() => showDeleteConfirm(record.id)}
                >
                  {t("Common-Delete")}
                </Menu.Item>
              )}
            </Menu>
          }
        >
          <Button className={styles.actionButtonMore}>
            <MoreOutlined className={styles.actionIconMore} />
          </Button>
        </Dropdown>
      ),
    },
  ];


  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={t("Common-Tickets")}
        icon={<RxCardStack />}
        showCreateButton={false}
        // additionalButtons={
        //   <Tooltip title={t("Common-Import")}>
        //     <Button
        //       className={styles.importButton}
        //       type="primary"
        //     >
        //       {t("Common-Import")}
        //     </Button>
        //   </Tooltip>
        // }
      />
      <div className={styles.contentWrapper}>
        <div className={styles.tableWrapper}>
          <div className={styles.headerTableWrapper}>
            <div className={styles.filtersWrapper}>
              <div className={styles.filterWrapper}>
                <Input
                  placeholder={t("Common-SearchByCode")}
                  className={styles.filterInput}
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => {
                    setCodeFilterValue(e.target.value);
                    setActualPage(1);
                  }}
                  value={codeFilterValue}
                />
              </div>
              <div className={styles.filterWrapper}>
                <Select
                  allowClear
                  className={styles.filterSelect}
                  showSearch
                  onSearch={handleSearchBranchOffices}
                  placeholder={t("Common-SearchByBranchOffice")}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onSelect={(_, option) => {
                    if (option) setBranchOfficeFilterValue(option.value);
                  }}
                  onClear={() => setBranchOfficeFilterValue(undefined)}
                  value={branchOfficeFilterValue}
                >
                  {getOptions(branchOffices)}
                </Select>
              </div>
              <div className={styles.filterWrapper}>
                <Select
                  allowClear
                  className={styles.filterSelect}
                  showSearch
                  onSearch={handleSearchClusters}
                  placeholder={t("Common-SearchByCluster")}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onSelect={(_, option) => {
                    if (option) setClusterFilterValue(option.value);
                  }}
                  onClear={() => setClusterFilterValue(undefined)}
                  value={clusterFilterValue}
                >
                  {getOptions(clusters)}
                </Select>
              </div>
              <div className={styles.filterWrapper}>
                <DatePicker.RangePicker
                  className={styles.dateSelector}
                  format="YYYY-MM-DD"
                  allowEmpty={[true, true]}
                  onChange={handleChangeDateFilter}
                  onCalendarChange={handleChangeDateFilter}
                  placeholder={[t("Common-Start"), t("Common-End")]}
                  value={
                    actualFilterStartDate && actualFilterEndDate
                      ? [moment(actualFilterStartDate, "YYYY-MM-DD"), moment(actualFilterEndDate, "YYYY-MM-DD")]
                      : undefined
                  }
                />
              </div>
              <div className={styles.filterWrapper}>
                <InputNumber
                  placeholder={t("Common-TotalFrom")}
                  className={styles.filterInputNumber}
                  onChange={(value) => {
                    setTotalLessFilterValue(value ?? undefined);
                    setActualPage(1);
                  }}
                  value={totalLessFilterValue}
                />
                <InputNumber
                  placeholder={t("Common-TotalTo")}
                  className={styles.filterInputNumber}
                  onChange={(value) => {
                    setTotalGreaterFilterValue(value ?? undefined);
                    setActualPage(1);
                  }}
                  value={totalGreaterFilterValue}
                />
              </div>
              <div className={styles.filterWrapper}>
                <Select
                  showSearch
                  allowClear
                  mode="multiple"
                  onClear={() => {
                    setSkusSelected([]);
                  }}
                  placeholder={t("Common-SearchSkuPlaceholder")}
                  onChange={(value) => {
                    setSkusSelected(value as []);
                  }}
                  className={styles.filterSelect}
                  defaultActiveFirstOption={false}
                  showArrow={false}
                  filterOption={false}
                  onSearch={handleSearchSkus}
                  options={(skuOptions || []).map((d: any) => ({
                    value: d.id,
                    label: d.name,
                  }))}
                  value={skusSelected}
                />
              </div>
              <div className={styles.filterWrapper}>
                <Select
                  allowClear
                  className={styles.filterSelect}
                  placeholder={t("Common-Type")}
                  onChange={(value) => {
                    setDocumentTypeFilterValue(value);
                    setActualPage(1);
                  }}
                  onClear={() => setDocumentTypeFilterValue(undefined)}
                  value={documentTypeFilterValue}
                >
                  <Select.Option value="CASH_TICKET">{t("Common-CashTicket")}</Select.Option>
                  <Select.Option value="CREDIT_INVOICE">{t("Common-CreditInvoice")}</Select.Option>
                  <Select.Option value="CREDIT_NOTE">{t("Common-CreditNote")}</Select.Option>
                </Select>
              </div>
              <div className={styles.filterWrapper}>
                <Select
                  allowClear
                  className={styles.filterSelect}
                  showSearch
                  onSearch={handleSearchClients}
                  placeholder={t("Common-SearchByClient")}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onSelect={(_, option) => {
                    if (option) setClientFilterValue(option.value);
                  }}
                  onClear={() => setClientFilterValue(undefined)}
                  value={clientFilterValue}
                >
                  {getOptions(clientOptions)}
                </Select>
              </div>
              {hasActiveFilters() && (
                <div className={styles.filterWrapper}>
                  <Tooltip title={t("Common-Clear")}>
                    <Button
                      onClick={handleClearFilters}
                      className={styles.clearButton}
                      icon={<ClearOutlined />}
                    >
                      {t("Common-Clear")}
                    </Button>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>

          <Table
            size="small"
            className={styles.table}
            rowKey={(record) => record.id}
            loading={loading}
            columns={columns}
            dataSource={data}
            pagination={{
              total: totalSize,
              showSizeChanger: false,
              onChange: handlePagination,
              current: actualPage,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
            }}
            locale={{
              emptyText: getEmptyState(),
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Tickets;
